import { Download, Eye, FileText } from 'lucide-react';
import { Quote } from '../../types/quotes.types';

interface QuotesTableProps {
  quotes: Quote[];
  hasActiveFilters: boolean;
  onReset: () => void;
}

export function QuotesTable({ quotes, hasActiveFilters, onReset }: QuotesTableProps) {
  if (quotes.length === 0) {
    return (
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-12 text-center flex flex-col items-center">
        <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-4 border border-slate-100 shadow-sm">
          <FileText className="w-5 h-5 text-slate-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-900">Aucun devis trouvé</h3>
        <p className="text-sm text-slate-500 mt-1 max-w-sm">Aucun devis ne correspond à vos critères.</p>
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="mt-4 text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 px-4 py-2 rounded-md transition-colors"
          >
            Afficher tous les devis
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
      <div className="overflow-x-auto -mx-px">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50/80 border-b border-slate-200">
            <tr>
              <th className="px-2 sm:px-3 lg:px-5 py-3.5 font-semibold text-slate-600 text-xs sm:text-sm whitespace-nowrap">
                Référence
              </th>
              <th className="px-2 sm:px-3 lg:px-5 py-3.5 font-semibold text-slate-600 text-xs sm:text-sm whitespace-nowrap">
                Client
              </th>
              <th className="px-2 sm:px-3 lg:px-5 py-3.5 font-semibold text-slate-600 text-xs sm:text-sm whitespace-nowrap hidden sm:table-cell">
                Espace
              </th>
              <th className="px-2 sm:px-3 lg:px-5 py-3.5 font-semibold text-slate-600 text-xs sm:text-sm whitespace-nowrap hidden md:table-cell">
                Création
              </th>
              <th className="px-2 sm:px-3 lg:px-5 py-3.5 font-semibold text-slate-600 text-xs sm:text-sm whitespace-nowrap">
                Montant
              </th>
              <th className="px-2 sm:px-3 lg:px-5 py-3.5 font-semibold text-slate-600 text-xs sm:text-sm whitespace-nowrap">
                Statut
              </th>
              <th className="px-2 sm:px-3 lg:px-5 py-3.5 font-semibold text-slate-600 text-xs sm:text-sm text-right whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {quotes.map((quo) => (
              <tr key={quo.id} className="hover:bg-slate-50/50 transition-colors group">
                <td className="px-2 sm:px-3 lg:px-5 py-3">
                  <span className="font-medium text-slate-900 text-xs sm:text-sm whitespace-nowrap">{quo.ref}</span>
                </td>
                <td className="px-2 sm:px-3 lg:px-5 py-3">
                  <span className="text-slate-700 font-medium text-xs sm:text-sm whitespace-nowrap">{quo.client}</span>
                </td>
                <td className="px-2 sm:px-3 lg:px-5 py-3 hidden sm:table-cell">
                  <span className="text-slate-600 text-xs sm:text-sm whitespace-nowrap">{quo.space}</span>
                </td>
                <td className="px-2 sm:px-3 lg:px-5 py-3 hidden md:table-cell">
                  <span className="text-slate-600 text-xs sm:text-sm whitespace-nowrap">{quo.date}</span>
                </td>
                <td className="px-2 sm:px-3 lg:px-5 py-3">
                  <span className="font-medium text-slate-900 text-xs sm:text-sm whitespace-nowrap">
                    {quo.amount.toLocaleString('fr-FR')} FCFA
                  </span>
                </td>
                <td className="px-2 sm:px-3 lg:px-5 py-3">
                  <StatusBadge status={quo.status} />
                </td>
                <td className="px-2 sm:px-3 lg:px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1 sm:gap-2 whitespace-nowrap">
                    {['QUOTE_REQUESTED', 'URGENT', 'OVERDUE', 'QUOTED'].includes(quo.status) ? (
                      <button
                        onClick={() => (window.location.href = `/quotes/${quo.id}/process`)}
                        className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary hover:text-white rounded-lg text-xs font-semibold transition-all border border-primary/20 shadow-sm"
                        title={quo.status === 'QUOTED' ? 'Voir / Renvoyer' : 'Générer Devis'}
                      >
                        <FileText className="w-3 sm:w-3.5 h-3 sm:h-3.5 flex-shrink-0" />
                        <span className="hidden lg:inline">
                          {quo.status === 'QUOTED' ? 'Voir / Renvoyer' : 'Générer Devis'}
                        </span>
                      </button>
                    ) : (
                      <button
                        onClick={() => (window.location.href = `/quotes/${quo.id}/process`)}
                        className="p-1.5 text-slate-400 hover:text-primary hover:bg-slate-100 rounded-md transition-colors"
                        title="Voir détails"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      className="p-1.5 text-slate-400 hover:text-primary hover:bg-slate-100 rounded-md transition-colors"
                      title="Télécharger version PDF"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; class: string; style?: React.CSSProperties }> = {
    // Statuts Devis (BookingStatus)
    QUOTE_REQUESTED: {
      label: 'En attente',
      class: 'text-[#00303c] border-[#dfc32f]',
      style: { backgroundColor: 'rgba(223, 195, 47, 0.15)' },
    },
    QUOTED: { label: 'Envoyé', class: 'text-white border-[#a4006c]', style: { backgroundColor: '#a4006c' } },
    URGENT: { label: 'Urgent', class: 'text-white border-[#a4006c]', style: { backgroundColor: '#a4006c' } },
    OVERDUE: { label: 'Expiré', class: 'text-white border-[#5c4738]', style: { backgroundColor: '#5c4738' } },
    PAID: { label: 'Payé', class: 'text-white border-[#00758d]', style: { backgroundColor: '#00758d' } },
    COMPLETED: { label: 'Payé', class: 'text-white border-[#00758d]', style: { backgroundColor: '#00758d' } },
    ACCEPTED: { label: 'Accepté', class: 'text-white border-[#00758d]', style: { backgroundColor: '#00758d' } },
    REJECTED: { label: 'Refusé', class: 'text-white border-[#5c4738]', style: { backgroundColor: '#5c4738' } },
    EXPIRED: { label: 'Expiré', class: 'text-white border-[#5c4738]', style: { backgroundColor: '#5c4738' } },
    // Anciens statuts (compatibilité)
    SENT: { label: 'Envoyé', class: 'text-white border-[#a4006c]', style: { backgroundColor: '#a4006c' } },
    DRAFT: {
      label: 'Brouillon',
      class: 'text-[#00303c] border-[#dfc32f]',
      style: { backgroundColor: 'rgba(223, 195, 47, 0.15)' },
    },
  };
  const config = configs[status] || {
    label: status,
    class: 'text-[#00303c] border-[#dfc32f]',
    style: { backgroundColor: 'rgba(223, 195, 47, 0.15)' },
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${config.class}`}
      style={config.style}
    >
      {config.label}
    </span>
  );
}
